import React, { useState } from 'react';

interface BrandLogoProps {
  variant?: 'full' | 'compact' | 'icon-only' | 'white' | 'dark';
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showSubtitle?: boolean;
  customLogoUrl?: string;
  customLogoUrlDark?: string;
  companyName?: string;
  tagline?: string;
  subtitle?: string;
}

export const BrandLogo: React.FC<BrandLogoProps> = ({
  variant = 'full',
  className = '',
  size = 'md',
  showSubtitle = true,
  customLogoUrl,
  customLogoUrlDark,
  companyName,
  tagline,
  subtitle,
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageDarkError, setImageDarkError] = useState(false);

  // Dimensions based on size
  const iconSizes = {
    sm: { w: 36, h: 36, imgClass: 'h-9 w-auto max-w-[120px]' },
    md: { w: 48, h: 48, imgClass: 'h-12 w-auto max-w-[150px]' },
    lg: { w: 64, h: 64, imgClass: 'h-16 w-auto max-w-[190px]' },
    xl: { w: 84, h: 84, imgClass: 'h-20 w-auto max-w-[240px]' },
  };

  const currentIcon = iconSizes[size];
  const displayCompanyName = companyName || 'CHERIF HADDAD';
  const displayTagline = tagline || 'Improving the Power of Your Company';
  const displaySubtitle = subtitle || 'Consulting - Audit - Expertise - Training - Certification';

  // SVG Globe Icon with the central ribbon and signature text
  const DefaultGlobeGraphic = (
    <svg
      width={currentIcon.w}
      height={currentIcon.h}
      viewBox="0 0 200 200"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="flex-shrink-0 transition-transform group-hover:scale-105 duration-300"
    >
      <defs>
        <linearGradient id="globeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#00A3C4" />
          <stop offset="45%" stopColor="#0D3B4C" />
          <stop offset="100%" stopColor="#071E28" />
        </linearGradient>
        <linearGradient id="ribbonGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#051C24" />
          <stop offset="50%" stopColor="#092A36" />
          <stop offset="100%" stopColor="#061922" />
        </linearGradient>
        <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feComposite in="SourceGraphic" in2="blur" operator="over" />
        </filter>
      </defs>

      {/* Outer Circle Ring */}
      <circle
        cx="100"
        cy="100"
        r="88"
        stroke="url(#globeGrad)"
        strokeWidth="11"
        fill="none"
      />

      {/* Parallels (Latitude lines) */}
      <ellipse
        cx="100"
        cy="100"
        rx="88"
        ry="42"
        stroke="url(#globeGrad)"
        strokeWidth="9"
        fill="none"
      />
      <ellipse
        cx="100"
        cy="100"
        rx="88"
        ry="74"
        stroke="url(#globeGrad)"
        strokeWidth="8"
        fill="none"
      />

      {/* Meridians (Longitude lines) */}
      <line
        x1="100"
        y1="12"
        x2="100"
        y2="188"
        stroke="url(#globeGrad)"
        strokeWidth="9"
      />
      <ellipse
        cx="100"
        cy="100"
        rx="46"
        ry="88"
        stroke="url(#globeGrad)"
        strokeWidth="9"
        fill="none"
      />

      {/* Central Ribbon Rectangle (covering the middle equator) */}
      <rect
        x="2"
        y="78"
        width="196"
        height="44"
        rx="8"
        fill="url(#ribbonGrad)"
        stroke="#00A3C4"
        strokeWidth="2.5"
      />

      {/* Signature Text inside ribbon: "Cherif Haddad" */}
      <text
        x="100"
        y="108"
        textAnchor="middle"
        fill="#FFFFFF"
        fontFamily="'Caveat', 'Great Vibes', 'Brush Script MT', cursive, sans-serif"
        fontSize="29"
        fontWeight="600"
        letterSpacing="1"
      >
        Cherif Haddad
      </text>
    </svg>
  );

  // Render graphic: either uploaded custom image or default text fallback if no image
  const hasLightLogo = customLogoUrl && !imageError;
  const hasDarkLogo = customLogoUrlDark && !imageDarkError;

  const LogoVisual = (hasLightLogo || hasDarkLogo) ? (
    <>
      {hasLightLogo && (
        <img
          src={customLogoUrl}
          alt={displayCompanyName}
          className={`${currentIcon.imgClass} object-contain transition-transform group-hover:scale-105 duration-300 ${hasDarkLogo ? 'dark:hidden block' : ''}`}
          referrerPolicy="no-referrer"
          onError={() => setImageError(true)}
        />
      )}
      {hasDarkLogo && (
        <img
          src={customLogoUrlDark}
          alt={displayCompanyName}
          className={`${currentIcon.imgClass} object-contain transition-transform group-hover:scale-105 duration-300 ${hasLightLogo ? 'hidden dark:block' : ''}`}
          referrerPolicy="no-referrer"
          onError={() => setImageDarkError(true)}
        />
      )}
    </>
  ) : (
    <div className="flex flex-col">
      <span className="font-montserrat font-black text-xl tracking-wide text-slate-900 dark:text-white uppercase transition-colors">
        {displayCompanyName}
      </span>
    </div>
  );

  return (
    <div className={`flex items-center gap-3 group ${className}`}>
      {LogoVisual}
    </div>
  );
};
